import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Check, X } from "lucide-react";
import { useAuth } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, errMsg } from "@/lib/api";

const RULES = [
  ["Almeno 8 caratteri", (p) => p.length >= 8],
  ["Una lettera maiuscola", (p) => /[A-Z]/.test(p)],
  ["Un numero", (p) => /\d/.test(p)],
  ["Un carattere speciale", (p) => /[^A-Za-z0-9]/.test(p)],
];

const finish = (ticket) => window.location.assign(`/sign-in?__clerk_ticket=${encodeURIComponent(ticket)}`);

const Shell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
    <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
      <h1 className="font-heading text-2xl font-bold">{title}</h1>
      <p className="text-slate-500 mt-1 mb-6">{subtitle}</p>
      {children}
      <p className="text-sm text-slate-500 mt-6 text-center">{footer}</p>
    </div>
  </div>
);

const useSubmit = (url) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (body) => {
    setBusy(true); setError("");
    try { finish((await axios.post(`${API}${url}`, body)).data.ticket); }
    catch (e) { setError(errMsg(e)); setBusy(false); }
  };
  return { busy, error, submit };
};

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={id} required {...props} /></div>
);

export function LoginPage() {
  const { isSignedIn } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const { busy, error, submit } = useSubmit("/auth/login");
  if (isSignedIn) return <Navigate to="/" replace />;
  return (
    <Shell title="Accedi a BuyNow" subtitle="Bentornato! Inserisci email e password."
      footer={<>Non hai un account? <Link to="/registrati" className="text-blue-600 font-medium" data-testid="login-to-register-link">Registrati</Link></>}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submit(f); }}>
        <Field id="login-email-input" label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field id="login-password-input" label="Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}
        <Button disabled={busy} data-testid="login-submit-button" className="w-full rounded-full bg-slate-900 hover:bg-slate-800">{busy ? "Accesso..." : "Accedi"}</Button>
      </form>
    </Shell>
  );
}

export function RegisterPage() {
  const { isSignedIn } = useAuth();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "" });
  const { busy, error, submit } = useSubmit("/auth/register");
  if (isSignedIn) return <Navigate to="/" replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = RULES.every(([, t]) => t(f.password)) && f.password === f.confirm;
  return (
    <Shell title="Crea il tuo account" subtitle="Dopo la registrazione caricherai i documenti per l'approvazione."
      footer={<>Hai già un account? <Link to="/accedi" className="text-blue-600 font-medium" data-testid="register-to-login-link">Accedi</Link></>}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); const { confirm, ...body } = f; submit(body); }}>
        <div className="grid grid-cols-2 gap-3">
          <Field id="register-first-name-input" label="Nome" value={f.first_name} onChange={set("first_name")} />
          <Field id="register-last-name-input" label="Cognome" value={f.last_name} onChange={set("last_name")} />
        </div>
        <Field id="register-email-input" label="Email" type="email" value={f.email} onChange={set("email")} />
        <Field id="register-password-input" label="Password" type="password" value={f.password} onChange={set("password")} />
        <ul className="grid grid-cols-2 gap-1 text-xs" data-testid="register-password-rules">
          {RULES.map(([label, test]) => {
            const ok = test(f.password);
            return <li key={label} className={`flex items-center gap-1 ${ok ? "text-emerald-600" : "text-slate-500"}`}>{ok ? <Check size={12} /> : <X size={12} />}{label}</li>;
          })}
        </ul>
        <Field id="register-confirm-input" label="Conferma password" type="password" value={f.confirm} onChange={set("confirm")} />
        {f.confirm && f.confirm !== f.password && <p className="text-xs text-rose-600" data-testid="register-mismatch">Le password non coincidono</p>}
        {error && <p className="text-sm text-rose-600" data-testid="register-error">{error}</p>}
        <Button disabled={busy || !valid} data-testid="register-submit-button" className="w-full rounded-full bg-slate-900 hover:bg-slate-800">{busy ? "Registrazione..." : "Crea account"}</Button>
      </form>
    </Shell>
  );
}
